<script setup>
import { ref, onMounted } from 'vue'
import { useOrganizationStore } from '@/stores/organizationStore'

// Use the Organization Store (Single Source of Truth)
const orgStore = useOrganizationStore()

// Local state for the form
const newOrgName = ref('')
const newSubscriptionPlan = ref('hobby')
const isCreating = ref(false)
const errorMsg = ref('')

// Load organizations when component mounts
onMounted(() => {
  if (orgStore.organizations.length === 0) {
    orgStore.fetchOrganizations()
  }
})

// Helper to format the role string (e.g., "organization_owner" -> "Owner")
const formatRole = (role) => {
  if (!role) return 'Member'
  if (role === 'organization_owner') return 'Owner'
  return role.replace(/_/g, ' ')
}

// Handle Creation Logic
const handleCreate = async () => {
  if (!newOrgName.value.trim()) return

  isCreating.value = true
  errorMsg.value = ''

  try {
    await orgStore.createOrganization(newOrgName.value, { plan: newSubscriptionPlan.value })
    newOrgName.value = ''
  } catch (err) {
    const apiMessage = err.response?.data?.error?.message;
    errorMsg.value = apiMessage || err.message || 'Failed to create organization.'
  } finally {
    isCreating.value = false
  }
}
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Organizations</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage your organization associations</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">Organizations</h2>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-gray-100">
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Role</th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Plan</th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Name</th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Owner</th>
              <th class="px-6 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="organization in orgStore.organizations" :key="organization.id"
              class="bg-white hover:bg-gray-50/50 transition border-b border-gray-50 last:border-0">
              <td class="px-6 py-3 font-medium text-gray-900 capitalize">
                {{ formatRole(organization.meta?.role) }}
              </td>
              <td class="px-6 py-3 font-bold text-gray-900 capitalize">
                {{ organization.subscription?.plan.replace('_', ' ') || 'Unkown' }}
              </td>
              <td class="px-6 py-3 font-medium text-gray-900">{{ organization.name }}</td>
              <td class="px-6 py-3 text-gray-500 text-xs">
                {{ (organization.meta?.role === 'organization_owner' ?
                  'You own this organization' : organization.ownerEmail) }}
              </td>
              <td class="px-6 py-3 text-right">
                <div class="inline-flex items-center gap-1">
                  <router-link :to="`/organizations/${organization.id}/users`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition" title="Users">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                    </svg>
                  </router-link>
                  <router-link :to="`/organizations/${organization.id}/teams`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition" title="Teams">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                    </svg>
                  </router-link>
                  <router-link :to="`/organizations/${organization.id}/analytics`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition" title="Usage">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605" />
                    </svg>
                  </router-link>
                  <router-link :to="`/organizations/${organization.id}/billing`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                    title="Billing">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                    </svg>
                  </router-link>
                  <router-link :to="`/organizations/${organization.id}/settings`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                    title="Settings">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-4 bg-gray-50 border-t border-gray-100">
        <p v-if="errorMsg" class="mb-2 text-xs text-red-600 font-medium px-1">{{ errorMsg }}</p>

        <form @submit.prevent="handleCreate" class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <select
            class="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9 sm:min-w-42 w-full sm:w-auto"
            v-model="newSubscriptionPlan">
            <option value="hobby">Hobby</option>
            <option value="pro_monthly" selected>Pro Monthly</option>
            <option value="pro_yearly">Pro Yearly</option>
          </select>

          <input v-model="newOrgName" type="text" placeholder="Name" :disabled="isCreating"
            class="grow px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9 disabled:bg-gray-100 disabled:text-gray-500" />

          <button type="submit" :disabled="isCreating || !newOrgName.trim()"
            class="bg-black text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition flex items-center h-9 disabled:opacity-70 disabled:cursor-not-allowed">
            <svg v-if="isCreating" class="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>

            <svg v-else class="w-3.5 h-3.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4" />
            </svg>

            {{ isCreating ? 'Creating...' : 'Create organization' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>